// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 중학교 수학 전 과정 커리큘럼 (교육부 2015 개정 교육과정 기반)
// 중1 ~ 중3  ·  5개 영역  ·  15개 챕터  ·  28개 유닛  ·  147개 토픽  ·  약 98시간
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export type Grade = 1 | 2 | 3
export type Domain = 'number' | 'algebra' | 'geometry' | 'statistics' | 'function'
export type Status = 'locked' | 'available' | 'in_progress' | 'completed'

export interface SubTopic {
  id: string          // 예: "N1-1-1"
  title: string       // 예: "자연수의 분류"
  estimatedMin: number // 예상 학습 시간(분)
  status: Status
}

export interface Unit {
  id: string           // 예: "N1-1"
  title: string        // 예: "소인수분해"
  emoji: string
  description: string
  story?: string       // 스토리텔링: "왜 배우는가?"
  topics: SubTopic[]
  status: Status
  isCurrent?: boolean  // 현재 학습 중
  contentReady?: boolean  // 콘텐츠 구현 완료 여부
  contentPath?: string    // 연결된 페이지 경로
}

export interface Chapter {
  id: string          // 예: "N1"
  domain: Domain
  grade: Grade
  title: string       // 예: "수와 연산"
  color: ChapterColor
  units: Unit[]
}

export interface ChapterColor {
  bg: string
  border: string
  badge: string
  accent: string
  light: string
  dot: string
}

// ── 도메인별 색상 팔레트 ──────────────────────────────────────────────────────
export const DOMAIN_COLORS: Record<Domain, ChapterColor> = {
  number: {
    bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700',
    accent: 'text-blue-700', light: 'bg-blue-500', dot: 'bg-blue-400',
  },
  algebra: {
    bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700',
    accent: 'text-violet-700', light: 'bg-violet-500', dot: 'bg-violet-400',
  },
  geometry: {
    bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700',
    accent: 'text-emerald-700', light: 'bg-emerald-500', dot: 'bg-emerald-400',
  },
  statistics: {
    bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700',
    accent: 'text-orange-700', light: 'bg-orange-500', dot: 'bg-orange-400',
  },
  function: {
    bg: 'bg-rose-50', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-700',
    accent: 'text-rose-700', light: 'bg-rose-500', dot: 'bg-rose-400',
  },
}

export const DOMAIN_LABELS: Record<Domain, string> = {
  number: '수와 연산',
  algebra: '문자와 식',
  geometry: '도형',
  statistics: '통계',
  function: '함수',
}

export const DOMAIN_EMOJIS: Record<Domain, string> = {
  number: '🔢',
  algebra: '🔣',
  geometry: '📐',
  statistics: '📊',
  function: '📈',
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 중학교 1학년
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export const GRADE1_CHAPTERS: Chapter[] = [
  // ── 수와 연산 ───────────────────────────────────────────────────────────────
  {
    id: 'N1', domain: 'number', grade: 1,
    title: '수와 연산',
    color: DOMAIN_COLORS.number,
    units: [
      {
        id: 'N1-1', title: '소인수분해', emoji: '🔑',
        description: '자연수를 소수의 곱으로 나타내고 최대공약수·최소공배수를 구한다',
        story: '"왜 12와 18의 최대공약수가 6이지?" 소인수분해를 알면 모든 자연수의 비밀이 보인다. 수포자가 가장 먼저 막히는 단원, 여기서 시작해 보자.',
        status: 'in_progress',
        isCurrent: true,
        contentReady: true,
        contentPath: '/unit/N1-1/learn',
        topics: [
          { id: 'N1-1-1', title: '소수와 합성수', estimatedMin: 30, status: 'completed' },
          { id: 'N1-1-2', title: '거듭제곱과 지수', estimatedMin: 25, status: 'completed' },
          { id: 'N1-1-3', title: '소인수분해 방법', estimatedMin: 40, status: 'completed' },
          { id: 'N1-1-4', title: '최대공약수(GCD)', estimatedMin: 40, status: 'completed' },
          { id: 'N1-1-5', title: '최소공배수(LCM)', estimatedMin: 40, status: 'completed' },
          { id: 'N1-1-6', title: 'GCD·LCM 활용 문제', estimatedMin: 45, status: 'completed' },
        ],
      },
      {
        id: 'N1-2', title: '정수와 유리수', emoji: '↔️',
        description: '음수를 포함한 정수와 유리수의 사칙연산을 마스터한다',
        status: 'completed',
        contentReady: true,
        isCurrent: true,
        contentPath: '/unit/N1-2/learn',
        topics: [
          { id: 'N1-2-1', title: '양수·음수·정수의 개념', estimatedMin: 30, status: 'completed' },
          { id: 'N1-2-2', title: '수직선과 절댓값', estimatedMin: 30, status: 'completed' },
          { id: 'N1-2-3', title: '정수의 대소 비교', estimatedMin: 25, status: 'completed' },
          { id: 'N1-2-4', title: '유리수의 개념과 분류', estimatedMin: 30, status: 'completed' },
          { id: 'N1-2-5', title: '정수·유리수의 덧셈·뺄셈', estimatedMin: 50, status: 'completed' },
          { id: 'N1-2-6', title: '정수·유리수의 곱셈·나눗셈', estimatedMin: 50, status: 'completed' },
          { id: 'N1-2-7', title: '사칙연산 혼합 계산', estimatedMin: 45, status: 'completed' },
        ],
      },
    ],
  },
  // ── 문자와 식 ───────────────────────────────────────────────────────────────
  {
    id: 'A1', domain: 'algebra', grade: 1,
    title: '문자와 식',
    color: DOMAIN_COLORS.algebra,
    units: [
      {
        id: 'A1-1', title: '문자의 사용과 식', emoji: '🔤',
        description: '문자를 사용하여 수량을 표현하고 식을 계산한다',
        status: 'completed',
        contentReady: true,
        isCurrent: true,
        contentPath: '/unit/A1-1/learn',
        story: '"x, y... 문자가 숫자를 대신하면 어떤 일이 생길까?" 편의점 알바부터 게임 개발까지, 문자 하나로 모든 수량을 표현하는 마법을 배워보자.',
        topics: [
          { id: 'A1-1-1', title: '문자를 사용한 식', estimatedMin: 30, status: 'completed' },
          { id: 'A1-1-2', title: '곱셈·나눗셈 기호의 생략', estimatedMin: 25, status: 'completed' },
          { id: 'A1-1-3', title: '식의 값 구하기', estimatedMin: 30, status: 'completed' },
          { id: 'A1-1-4', title: '일차식과 동류항', estimatedMin: 35, status: 'completed' },
          { id: 'A1-1-5', title: '일차식의 덧셈·뺄셈', estimatedMin: 40, status: 'completed' },
          { id: 'A1-1-6', title: '일차식의 곱셈·나눗셈', estimatedMin: 40, status: 'completed' },
        ],
      },
      {
        id: 'A1-2', title: '일차방정식', emoji: '⚖️',
        description: '등식의 성질을 이용하여 일차방정식을 풀고 활용한다',
        contentReady: true,
        status: 'in_progress',
        topics: [
          { id: 'A1-2-1', title: '방정식과 해의 개념', estimatedMin: 30, status: 'completed' },
          { id: 'A1-2-2', title: '등식의 성질', estimatedMin: 35, status: 'completed' },
          { id: 'A1-2-3', title: '이항과 일차방정식 풀이', estimatedMin: 45, status: 'in_progress' },
          { id: 'A1-2-4', title: '일차방정식 활용 (거리·속력·시간)', estimatedMin: 60, status: 'available' },
          { id: 'A1-2-5', title: '일차방정식 활용 (농도·비율)', estimatedMin: 60, status: 'locked' },
        ],
      },
    ],
  },
  // ── 도형 ────────────────────────────────────────────────────────────────────
  {
    id: 'G1', domain: 'geometry', grade: 1,
    title: '도형',
    color: DOMAIN_COLORS.geometry,
    units: [
      {
        id: 'G1-1', title: '기본 도형', emoji: '📏',
        description: '점·선·면·각의 기본 개념과 위치 관계를 이해한다',
        contentReady: true,
        status: 'available',
        topics: [
          { id: 'G1-1-1', title: '점·선·면의 개념', estimatedMin: 25, status: 'available' },
          { id: 'G1-1-2', title: '각의 종류와 측정', estimatedMin: 30, status: 'locked' },
          { id: 'G1-1-3', title: '평행선과 교선', estimatedMin: 35, status: 'locked' },
          { id: 'G1-1-4', title: '두 직선의 위치 관계', estimatedMin: 30, status: 'locked' },
          { id: 'G1-1-5', title: '동위각·엇각', estimatedMin: 35, status: 'locked' },
        ],
      },
      {
        id: 'G1-2', title: '평면도형', emoji: '🔷',
        description: '다각형과 원의 성질, 넓이를 계산한다',
        contentReady: true,
        status: 'locked',
        topics: [
          { id: 'G1-2-1', title: '다각형의 내각·외각의 합', estimatedMin: 40, status: 'locked' },
          { id: 'G1-2-2', title: '삼각형의 종류와 성질', estimatedMin: 35, status: 'locked' },
          { id: 'G1-2-3', title: '부채꼴의 호와 넓이', estimatedMin: 45, status: 'locked' },
          { id: 'G1-2-4', title: '원의 둘레와 넓이', estimatedMin: 35, status: 'locked' },
        ],
      },
      {
        id: 'G1-3', title: '입체도형', emoji: '🧊',
        description: '다면체와 회전체의 겉넓이·부피를 구한다',
        status: 'locked',
        contentReady: true,
        topics: [
          { id: 'G1-3-1', title: '다면체의 분류 (각기둥·각뿔)', estimatedMin: 35, status: 'locked' },
          { id: 'G1-3-2', title: '회전체 (원기둥·원뿔·구)', estimatedMin: 35, status: 'locked' },
          { id: 'G1-3-3', title: '겉넓이 계산', estimatedMin: 50, status: 'locked' },
          { id: 'G1-3-4', title: '부피 계산', estimatedMin: 50, status: 'locked' },
        ],
      },
    ],
  },
  // ── 함수 (정비례·반비례) ─────────────────────────────────────────────────────
  {
    id: 'F1', domain: 'function', grade: 1,
    title: '함수 (정비례·반비례)',
    color: DOMAIN_COLORS.function,
    units: [
      {
        id: 'F1-1', title: '정비례와 반비례', emoji: '📉',
        description: '정비례·반비례 관계를 이해하고 그래프를 그린다. 일차함수(중2)의 핵심 선수 개념.',
        story: '속력이 일정할 때 시간과 거리의 관계, 넓이가 일정할 때 가로와 세로의 관계— 우리 주변의 두 양이 어떻게 연결되는지 그래프로 확인한다.',
        status: 'locked',
        contentReady: true,
        topics: [
          { id: 'F1-1-1', title: '함수의 뜻과 표현 (대응, 식, 표, 그래프)', estimatedMin: 30, status: 'locked' },
          { id: 'F1-1-2', title: '정비례 관계 y = ax', estimatedMin: 35, status: 'locked' },
          { id: 'F1-1-3', title: '정비례 그래프 (원점 통과 직선)', estimatedMin: 35, status: 'locked' },
          { id: 'F1-1-4', title: '반비례 관계 y = a/x', estimatedMin: 35, status: 'locked' },
          { id: 'F1-1-5', title: '반비례 그래프 (쌍곡선)', estimatedMin: 35, status: 'locked' },
          { id: 'F1-1-6', title: '정비례·반비례 활용 문제', estimatedMin: 45, status: 'locked' },
        ],
      },
    ],
  },
  // ── 통계 ────────────────────────────────────────────────────────────────────
  {
    id: 'S1', domain: 'statistics', grade: 1,
    title: '통계',
    color: DOMAIN_COLORS.statistics,
    units: [
      {
        id: 'S1-1', title: '자료의 정리와 해석', emoji: '📊',
        description: '도수분포표, 히스토그램, 줄기잎그림으로 자료를 정리한다',
        status: 'available',
        contentReady: true,
        topics: [
          { id: 'S1-1-1', title: '변수와 자료의 종류', estimatedMin: 25, status: 'available' },
          { id: 'S1-1-2', title: '줄기잎 그림', estimatedMin: 30, status: 'available' },
          { id: 'S1-1-3', title: '도수분포표', estimatedMin: 40, status: 'available' },
          { id: 'S1-1-4', title: '히스토그램과 도수분포다각형', estimatedMin: 45, status: 'available' },
          { id: 'S1-1-5', title: '상대도수와 누적도수', estimatedMin: 40, status: 'available' },
        ],
      },
    ],
  },
]

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 중학교 2학년
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export const GRADE2_CHAPTERS: Chapter[] = [
  {
    id: 'N2', domain: 'number', grade: 2,
    title: '수와 연산',
    color: DOMAIN_COLORS.number,
    units: [
      {
        id: 'N2-1', title: '유리수와 순환소수', emoji: '🔄',
        description: '유리수와 순환소수의 관계, 변환 방법을 이해한다',
        status: 'available',
        contentReady: true,
        topics: [
          { id: 'N2-1-1', title: '유한소수·무한소수', estimatedMin: 30, status: 'available' },
          { id: 'N2-1-2', title: '순환소수의 표현', estimatedMin: 30, status: 'available' },
          { id: 'N2-1-3', title: '순환소수를 분수로 변환', estimatedMin: 45, status: 'available' },
          { id: 'N2-1-4', title: '유리수와 소수의 관계', estimatedMin: 35, status: 'available' },
        ],
      },
      {
        id: 'N2-2', title: '제곱근과 실수', emoji: '√',
        description: '제곱근의 뜻과 성질, 무리수와 실수의 분류 체계를 완전히 이해한다',
        story: '\"루트 2는 왜 분수로 나타낼 수 없을까?\" 유리수의 세계 밖에 더 넓은 수의 세계가 있다. 제곱근이라는 새로운 도구로 실수의 전체 지도를 그려보자.',
        status: 'available',
        isCurrent: true,
        contentReady: true,
        contentPath: '/unit/N2-2/learn',
        topics: [
          { id: 'N2-2-1', title: '제곱근의 뜻과 표현', estimatedMin: 30, status: 'available' },
          { id: 'N2-2-2', title: '제곱근의 성질', estimatedMin: 35, status: 'available' },
          { id: 'N2-2-3', title: '무리수와 실수', estimatedMin: 30, status: 'available' },
          { id: 'N2-2-4', title: '실수의 대소 비교', estimatedMin: 35, status: 'available' },
          { id: 'N2-2-5', title: '제곱근의 곱셈·나눗셈', estimatedMin: 40, status: 'available' },
          { id: 'N2-2-6', title: '제곱근의 덧셈·뺄셈', estimatedMin: 40, status: 'available' },
        ],
      },
    ],
  },
  {
    id: 'A2', domain: 'algebra', grade: 2,
    title: '문자와 식',
    color: DOMAIN_COLORS.algebra,
    units: [
      {
        id: 'A2-1', title: '단항식과 다항식', emoji: '🧮',
        description: '단항식·다항식의 개념과 사칙연산을 익힌다',
        status: 'available',
        contentReady: true,
        topics: [
          { id: 'A2-1-1', title: '지수법칙', estimatedMin: 40, status: 'available' },
          { id: 'A2-1-2', title: '단항식의 곱셈·나눗셈', estimatedMin: 40, status: 'available' },
          { id: 'A2-1-3', title: '다항식의 덧셈·뺄셈', estimatedMin: 35, status: 'available' },
          { id: 'A2-1-4', title: '다항식의 곱셈 (분배법칙)', estimatedMin: 45, status: 'available' },
          { id: 'A2-1-5', title: '곱셈 공식 4가지', estimatedMin: 60, status: 'available' },
        ],
      },
      {
        id: 'A2-2', title: '일차부등식', emoji: '↗️',
        description: '부등식의 성질을 이용하여 일차부등식을 풀고 활용한다',
        status: 'available',
        contentReady: true,
        topics: [
          { id: 'A2-2-1', title: '부등식의 개념과 성질', estimatedMin: 35, status: 'available' },
          { id: 'A2-2-2', title: '일차부등식 풀기', estimatedMin: 45, status: 'available' },
          { id: 'A2-2-3', title: '부등식의 해를 수직선으로 표현', estimatedMin: 30, status: 'available' },
          { id: 'A2-2-4', title: '일차부등식 활용', estimatedMin: 60, status: 'available' },
        ],
      },
      {
        id: 'A2-3', title: '연립일차방정식', emoji: '🔀',
        description: '미지수 2개인 연립방정식의 풀이와 활용',
        status: 'available',
        contentReady: true,
        topics: [
          { id: 'A2-3-1', title: '미지수 2개의 일차방정식', estimatedMin: 30, status: 'available' },
          { id: 'A2-3-2', title: '연립방정식의 개념과 해', estimatedMin: 30, status: 'available' },
          { id: 'A2-3-3', title: '가감법', estimatedMin: 45, status: 'available' },
          { id: 'A2-3-4', title: '대입법', estimatedMin: 45, status: 'available' },
          { id: 'A2-3-5', title: '연립방정식 활용 (수·가격·나이 문제)', estimatedMin: 60, status: 'available' },
          { id: 'A2-3-6', title: '연립방정식 활용 (거리·속력·시간)', estimatedMin: 60, status: 'available' },
        ],
      },
    ],
  },
  {
    id: 'F2', domain: 'function', grade: 2,
    title: '함수',
    color: DOMAIN_COLORS.function,
    units: [
      {
        id: 'F2-1', title: '일차함수와 그래프', emoji: '📈',
        description: '일차함수의 개념, 그래프, 성질을 완전히 이해한다',
        story: '택시 요금, 핸드폰 요금제, 자동차 여행… 우리 주변의 모든 "일정하게 변하는 것"은 일차함수로 설명된다. y = ax + b 하나로 미래를 예측하는 법을 배운다.',
        status: 'in_progress',
        isCurrent: true,
        contentReady: true,
        contentPath: '/learn',
        topics: [
          { id: 'F2-1-1', title: '변수와 함수의 개념', estimatedMin: 30, status: 'completed' },
          { id: 'F2-1-2', title: '순서쌍과 좌표평면', estimatedMin: 30, status: 'completed' },
          { id: 'F2-1-3', title: '표에서 함수 관계 찾기', estimatedMin: 35, status: 'completed' },
          { id: 'F2-1-4', title: '변화율과 기울기', estimatedMin: 40, status: 'completed' },
          { id: 'F2-1-5', title: '기울기의 의미와 부호', estimatedMin: 35, status: 'completed' },
          { id: 'F2-1-6', title: 'y절편과 초기값', estimatedMin: 35, status: 'completed' },
          { id: 'F2-1-7', title: '일차함수식 y = ax + b 완성', estimatedMin: 45, status: 'completed' },
          { id: 'F2-1-8', title: '정비례 vs 일차함수', estimatedMin: 35, status: 'completed' },
          { id: 'F2-1-9', title: '그래프 그리기 (두 점법·절편법)', estimatedMin: 45, status: 'completed' },
          { id: 'F2-1-10', title: '그래프에서 식 구하기', estimatedMin: 45, status: 'completed' },
          { id: 'F2-1-11', title: '실생활 문장제 → 일차함수', estimatedMin: 60, status: 'completed' },
          { id: 'F2-1-12', title: '오답 분석과 오개념 교정', estimatedMin: 50, status: 'in_progress' },
        ],
      },
      {
        id: 'F2-2', title: '일차함수와 일차방정식', emoji: '🔗',
        description: '일차함수와 일차방정식의 관계, 연립방정식의 그래프 해법',
        status: 'available',
        contentReady: true,
        topics: [
          { id: 'F2-2-1', title: '일차방정식과 직선의 관계', estimatedMin: 40, status: 'available' },
          { id: 'F2-2-2', title: '기울기·절편으로 직선 결정', estimatedMin: 35, status: 'available' },
          { id: 'F2-2-3', title: '두 직선의 위치 관계', estimatedMin: 40, status: 'available' },
          { id: 'F2-2-4', title: '연립방정식과 교점', estimatedMin: 45, status: 'available' },
          { id: 'F2-2-5', title: '그래프로 연립방정식 풀기', estimatedMin: 45, status: 'available' },
          { id: 'F2-2-6', title: '해의 특수한 경우(평행·일치)', estimatedMin: 40, status: 'available' },
          { id: 'F2-2-7', title: '계수 조건과 해의 종류', estimatedMin: 40, status: 'available' },
          { id: 'F2-2-8', title: '실생활 연립방정식 활용', estimatedMin: 50, status: 'available' },
          { id: 'F2-2-9', title: '미지수 포함 직선 결정', estimatedMin: 45, status: 'available' },
          { id: 'F2-2-10', title: '종합 오답 분석', estimatedMin: 50, status: 'available' },
        ],
      },
    ],
  },
  {
    id: 'G2', domain: 'geometry', grade: 2,
    title: '도형',
    color: DOMAIN_COLORS.geometry,
    units: [
      {
        id: 'G2-1', title: '삼각형의 성질', emoji: '🔺',
        description: '이등변삼각형과 직각삼각형의 합동 조건과 성질',
        status: 'locked',
        topics: [
          { id: 'G2-1-1', title: '삼각형의 합동 조건', estimatedMin: 40, status: 'locked' },
          { id: 'G2-1-2', title: '이등변삼각형의 성질', estimatedMin: 35, status: 'locked' },
          { id: 'G2-1-3', title: '직각삼각형의 합동 (RHS·RHA)', estimatedMin: 40, status: 'locked' },
        ],
      },
      {
        id: 'G2-2', title: '사각형의 성질', emoji: '🔲',
        description: '평행사변형, 직사각형, 마름모, 정사각형의 성질',
        status: 'locked',
        topics: [
          { id: 'G2-2-1', title: '평행사변형의 성질과 조건', estimatedMin: 45, status: 'locked' },
          { id: 'G2-2-2', title: '직사각형·마름모·정사각형', estimatedMin: 45, status: 'locked' },
          { id: 'G2-2-3', title: '여러 사각형의 포함 관계', estimatedMin: 35, status: 'locked' },
          { id: 'G2-2-4', title: '사다리꼴과 등변사다리꼴', estimatedMin: 35, status: 'locked' },
        ],
      },
      {
        id: 'G2-3', title: '도형의 닮음', emoji: '🔍',
        description: '닮음의 개념, 닮음비, 닮음 조건과 활용',
        status: 'locked',
        topics: [
          { id: 'G2-3-1', title: '닮음의 개념과 닮음비', estimatedMin: 35, status: 'locked' },
          { id: 'G2-3-2', title: '삼각형의 닮음 조건 (AA·SAS·SSS)', estimatedMin: 45, status: 'locked' },
          { id: 'G2-3-3', title: '평행선과 선분의 비', estimatedMin: 45, status: 'locked' },
          { id: 'G2-3-4', title: '삼각형의 중점 연결 정리', estimatedMin: 40, status: 'locked' },
          { id: 'G2-3-5', title: '닮음의 활용 (지도·축척)', estimatedMin: 40, status: 'locked' },
        ],
      },
    ],
  },
  {
    id: 'S2', domain: 'statistics', grade: 2,
    title: '통계',
    color: DOMAIN_COLORS.statistics,
    units: [
      {
        id: 'S2-1', title: '경우의 수', emoji: '🎲',
        description: '합의 법칙·곱의 법칙으로 경우의 수를 구한다',
        status: 'locked',
        topics: [
          { id: 'S2-1-1', title: '사건과 경우의 수', estimatedMin: 30, status: 'locked' },
          { id: 'S2-1-2', title: '합의 법칙', estimatedMin: 30, status: 'locked' },
          { id: 'S2-1-3', title: '곱의 법칙', estimatedMin: 30, status: 'locked' },
          { id: 'S2-1-4', title: '순열 (줄 세우기)', estimatedMin: 45, status: 'locked' },
          { id: 'S2-1-5', title: '조합 (뽑기)', estimatedMin: 45, status: 'locked' },
        ],
      },
      {
        id: 'S2-2', title: '확률', emoji: '🎯',
        description: '확률의 뜻, 성질, 여사건과 조건부확률',
        status: 'locked',
        topics: [
          { id: 'S2-2-1', title: '확률의 뜻과 기본 성질', estimatedMin: 35, status: 'locked' },
          { id: 'S2-2-2', title: '여사건의 확률', estimatedMin: 30, status: 'locked' },
          { id: 'S2-2-3', title: '확률의 덧셈 법칙', estimatedMin: 35, status: 'locked' },
          { id: 'S2-2-4', title: '확률의 곱셈 법칙', estimatedMin: 35, status: 'locked' },
        ],
      },
    ],
  },
]

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 중학교 3학년
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export const GRADE3_CHAPTERS: Chapter[] = [
  {
    id: 'N3', domain: 'number', grade: 3,
    title: '수와 연산',
    color: DOMAIN_COLORS.number,
    units: [
      {
        id: 'N3-1', title: '실수와 무리수', emoji: '√',
        description: '제곱근, 무리수, 실수의 개념과 연산',
        status: 'locked',
        topics: [
          { id: 'N3-1-1', title: '제곱근의 뜻과 표현', estimatedMin: 35, status: 'locked' },
          { id: 'N3-1-2', title: '제곱근의 성질', estimatedMin: 35, status: 'locked' },
          { id: 'N3-1-3', title: '무리수와 실수의 분류', estimatedMin: 30, status: 'locked' },
          { id: 'N3-1-4', title: '실수의 대소 비교', estimatedMin: 30, status: 'locked' },
          { id: 'N3-1-5', title: '근호를 포함한 식의 계산', estimatedMin: 50, status: 'locked' },
          { id: 'N3-1-6', title: '분모의 유리화', estimatedMin: 40, status: 'locked' },
        ],
      },
    ],
  },
  {
    id: 'A3', domain: 'algebra', grade: 3,
    title: '문자와 식',
    color: DOMAIN_COLORS.algebra,
    units: [
      {
        id: 'A3-1', title: '다항식의 인수분해', emoji: '🧩',
        description: '곱셈 공식의 역을 이용하여 다항식을 인수분해한다',
        status: 'locked',
        topics: [
          { id: 'A3-1-1', title: '인수분해의 뜻과 공통 인수', estimatedMin: 30, status: 'locked' },
          { id: 'A3-1-2', title: 'a² ± 2ab + b² 공식', estimatedMin: 40, status: 'locked' },
          { id: 'A3-1-3', title: 'a² - b² 공식', estimatedMin: 35, status: 'locked' },
          { id: 'A3-1-4', title: 'x² + (a+b)x + ab 공식', estimatedMin: 45, status: 'locked' },
          { id: 'A3-1-5', title: 'acx² + (ad+bc)x + bd 공식', estimatedMin: 50, status: 'locked' },
          { id: 'A3-1-6', title: '인수분해 활용', estimatedMin: 45, status: 'locked' },
        ],
      },
      {
        id: 'A3-2', title: '이차방정식', emoji: '🎢',
        description: '이차방정식의 풀이 방법 4가지와 판별식 활용',
        status: 'locked',
        topics: [
          { id: 'A3-2-1', title: '이차방정식의 개념', estimatedMin: 30, status: 'locked' },
          { id: 'A3-2-2', title: '인수분해를 이용한 풀이', estimatedMin: 45, status: 'locked' },
          { id: 'A3-2-3', title: '제곱근을 이용한 풀이', estimatedMin: 40, status: 'locked' },
          { id: 'A3-2-4', title: '완전제곱식을 이용한 풀이', estimatedMin: 45, status: 'locked' },
          { id: 'A3-2-5', title: '근의 공식', estimatedMin: 50, status: 'locked' },
          { id: 'A3-2-6', title: '판별식 D와 근의 개수', estimatedMin: 40, status: 'locked' },
          { id: 'A3-2-7', title: '이차방정식 활용 (넓이·속도·수)', estimatedMin: 60, status: 'locked' },
        ],
      },
    ],
  },
  {
    id: 'F3', domain: 'function', grade: 3,
    title: '함수',
    color: DOMAIN_COLORS.function,
    units: [
      {
        id: 'F3-1', title: '이차함수와 그래프', emoji: '🎠',
        description: '이차함수의 그래프(포물선)와 꼭짓점, 축의 방정식',
        status: 'locked',
        topics: [
          { id: 'F3-1-1', title: 'y = ax² 의 그래프', estimatedMin: 40, status: 'locked' },
          { id: 'F3-1-2', title: 'y = ax² + q 의 평행이동', estimatedMin: 40, status: 'locked' },
          { id: 'F3-1-3', title: 'y = a(x-p)² 의 평행이동', estimatedMin: 40, status: 'locked' },
          { id: 'F3-1-4', title: '표준형 y = a(x-p)² + q', estimatedMin: 45, status: 'locked' },
          { id: 'F3-1-5', title: '일반형 y = ax² + bx + c', estimatedMin: 50, status: 'locked' },
          { id: 'F3-1-6', title: '꼭짓점과 축의 방정식', estimatedMin: 40, status: 'locked' },
          { id: 'F3-1-7', title: '이차함수의 최댓값·최솟값', estimatedMin: 45, status: 'locked' },
          { id: 'F3-1-8', title: '이차함수 활용 (넓이·포물선·물리)', estimatedMin: 60, status: 'locked' },
        ],
      },
    ],
  },
  {
    id: 'G3', domain: 'geometry', grade: 3,
    title: '도형',
    color: DOMAIN_COLORS.geometry,
    units: [
      {
        id: 'G3-1', title: '피타고라스 정리', emoji: '📐',
        description: '직각삼각형의 세 변의 관계와 다양한 활용',
        status: 'locked',
        topics: [
          { id: 'G3-1-1', title: '피타고라스 정리 증명', estimatedMin: 40, status: 'locked' },
          { id: 'G3-1-2', title: '피타고라스 정리 역(逆)', estimatedMin: 35, status: 'locked' },
          { id: 'G3-1-3', title: '평면도형 넓이 활용', estimatedMin: 45, status: 'locked' },
          { id: 'G3-1-4', title: '입체도형 대각선 활용', estimatedMin: 45, status: 'locked' },
          { id: 'G3-1-5', title: '특수 삼각형 (30-60-90, 45-45-90)', estimatedMin: 40, status: 'locked' },
        ],
      },
      {
        id: 'G3-2', title: '원의 성질', emoji: '⭕',
        description: '원과 직선, 원의 접선, 원주각과 중심각',
        status: 'locked',
        topics: [
          { id: 'G3-2-1', title: '원과 직선의 위치 관계', estimatedMin: 35, status: 'locked' },
          { id: 'G3-2-2', title: '원의 접선과 접선의 길이', estimatedMin: 40, status: 'locked' },
          { id: 'G3-2-3', title: '원주각과 중심각', estimatedMin: 45, status: 'locked' },
          { id: 'G3-2-4', title: '원에 내접하는 사각형', estimatedMin: 40, status: 'locked' },
          { id: 'G3-2-5', title: '접선과 현이 이루는 각', estimatedMin: 40, status: 'locked' },
        ],
      },
    ],
  },
  {
    id: 'S3', domain: 'statistics', grade: 3,
    title: '통계',
    color: DOMAIN_COLORS.statistics,
    units: [
      {
        id: 'S3-1', title: '대푯값과 산포도', emoji: '📉',
        description: '평균·중앙값·최빈값과 분산·표준편차를 구하고 해석한다',
        status: 'locked',
        topics: [
          { id: 'S3-1-1', title: '평균·중앙값·최빈값', estimatedMin: 40, status: 'locked' },
          { id: 'S3-1-2', title: '편차와 분산', estimatedMin: 45, status: 'locked' },
          { id: 'S3-1-3', title: '표준편차', estimatedMin: 40, status: 'locked' },
          { id: 'S3-1-4', title: '산포도 비교와 해석', estimatedMin: 40, status: 'locked' },
        ],
      },
      {
        id: 'S3-2', title: '상관관계', emoji: '🔗',
        description: '산점도와 상관관계를 통해 두 변수의 관계를 분석한다',
        status: 'locked',
        topics: [
          { id: 'S3-2-1', title: '산점도의 뜻과 작성', estimatedMin: 35, status: 'locked' },
          { id: 'S3-2-2', title: '상관관계의 종류 (양·음·무)', estimatedMin: 35, status: 'locked' },
          { id: 'S3-2-3', title: '상관관계의 강도 해석', estimatedMin: 35, status: 'locked' },
        ],
      },
    ],
  },
]

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 전체 통합
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export const ALL_CHAPTERS: Chapter[] = [
  ...GRADE1_CHAPTERS,
  ...GRADE2_CHAPTERS,
  ...GRADE3_CHAPTERS,
]

// ── 통계 유틸리티 ─────────────────────────────────────────────────────────────
export function calcStats() {
  let totalTopics = 0
  let completedTopics = 0
  let inProgressTopics = 0
  let totalMinutes = 0
  let completedMinutes = 0

  for (const ch of ALL_CHAPTERS) {
    for (const unit of ch.units) {
      for (const t of unit.topics) {
        totalTopics++
        totalMinutes += t.estimatedMin
        if (t.status === 'completed') { completedTopics++; completedMinutes += t.estimatedMin }
        if (t.status === 'in_progress') inProgressTopics++
      }
    }
  }

  const totalUnits = ALL_CHAPTERS.reduce((s, c) => s + c.units.length, 0)
  const completedUnits = ALL_CHAPTERS.reduce(
    (s, c) => s + c.units.filter(u => u.status === 'completed').length, 0
  )

  return {
    totalTopics,
    completedTopics,
    inProgressTopics,
    totalMinutes,
    completedMinutes,
    totalUnits,
    completedUnits,
    pct: Math.round((completedTopics / totalTopics) * 100),
    totalHours: Math.round(totalMinutes / 60),
    completedHours: Math.round(completedMinutes / 60),
  }
}

export function getGradeChapters(grade: Grade): Chapter[] {
  return ALL_CHAPTERS.filter(c => c.grade === grade)
}

export function getCurrentUnit(): Unit | null {
  for (const ch of ALL_CHAPTERS) {
    const u = ch.units.find(u => u.isCurrent)
    if (u) return u
  }
  return null
}

export function getUnitById(unitId: string): { chapter: Chapter; unit: Unit } | null {
  for (const ch of ALL_CHAPTERS) {
    const u = ch.units.find(u => u.id === unitId)
    if (u) return { chapter: ch, unit: u }
  }
  return null
}

export const GRADE_LABELS: Record<Grade, string> = {
  1: '중학교 1학년',
  2: '중학교 2학년',
  3: '중학교 3학년',
}

export const GRADE_SHORT: Record<Grade, string> = {
  1: '중1',
  2: '중2',
  3: '중3',
}
